// Confirmação manual de pagamento (equipe anexa o comprovante) — usada por
// CobrancaEnviadaModal e HistoricoCobrancasTab, que precisam gravar igual.
//
// Por que existe: até 13/08/2026 esses dois caminhos gravavam só
// `status_boleto_vigor='Pago'` e deixavam `data_pgto_boleto` vazio. Resultado:
// 209 boletos pagos sem data de pagamento, que qualquer relatório derivando
// status por data classificava como vencidos (chamado #3789). A Meryene
// confirmou em 13/08 que a equipe confirma "no mesmo dia ou dias depois" — por
// isso a data é escolhida por quem confirma, e não `now()`.
//
// ⚠️ A pegadinha do boleto único: o trigger `trg_enfileirar_pagamento_equatorial`
// dispara em UPDATE OF `data_pgto_boleto` e, quando a UC tem `boleto_unico`,
// insere na `pagamento_equatorial_fila` — que paga a conta da Equatorial de
// verdade. A regra de negócio (comentada em CobrancaEnviadaModal desde antes
// disto) é que **confirmação manual não dispara repasse automático**: só a
// confirmação via webhook do banco faz isso, porque comprovante anexado é
// evidência mais fraca que baixa bancária. Então em UC aderida seguimos sem
// gravar a data e apenas sinalizamos `status_repasse_equatorial='pendente'`.
// Na prática isso não custa nada hoje: das 209 faturas pagas sem data, **zero**
// são de UC aderida. Se um dia precisar valer para elas também, o caminho é
// ensinar o trigger a distinguir a origem — não remover essa guarda daqui.
//
// O valor pago (`valor_pagamento`) entra desde 13/10/2026 (chamado #6247): sem
// ele a fatura saía na planilha de Faturas com "Valor Pago (recebido)" vazio,
// diferente da baixa do Sicoob e do Asaas. Vale também na UC de boleto único —
// o gatilho de repasse olha só a data, não o valor.

// O import do cliente Supabase é DINÂMICO de propósito. `montarCamposConfirmacao`
// é a guarda que impede um repasse indevido e precisa de teste; com `import
// { supabase } from './supabase'` no topo, `node --test` quebra ao resolver o
// caminho sem extensão (o alias é do Vite, não do Node) e o arquivo inteiro
// vira intestável. Mesma restrição documentada em
// views/Financeiro/PainelExecutivo/lib/filtrarFaturas.ts.

export interface ResultadoConfirmacaoManual {
  /** Campos para o `.update()` em `faturas`. */
  camposUpdate: Record<string, unknown>;
  /** true quando a UC aderiu ao boleto único e a data foi omitida de propósito. */
  ucBoletoUnico: boolean;
}

/** A UC desta fatura aderiu ao boleto único? Decide se a data pode ser gravada. */
export async function ucAderiuAoBoletoUnico(ucCodigo: string | null | undefined): Promise<boolean> {
  if (!ucCodigo) return false;
  const { supabase } = await import('./supabase');
  const { data } = await supabase
    .from('unidades_consumidoras')
    .select('boleto_unico, numero_uc, outro_numero_uc')
    .or(`numero_uc.eq.${ucCodigo},outro_numero_uc.eq.${ucCodigo}`);
  return (data || []).some((uc: any) => uc.boleto_unico === true);
}

/**
 * Decide o que gravar. Pura de propósito — é ela que segura o gatilho de
 * repasse, então precisa ser testável sem banco.
 * @param dataPagamento data informada pela equipe, no formato `yyyy-mm-dd`.
 * @param valorPago valor que consta no comprovante; null não grava nada.
 */
export function montarCamposConfirmacao(
  ucBoletoUnico: boolean,
  comprovanteUrl: string,
  dataPagamento: string,
  valorPago: number | null = null
): Record<string, unknown> {
  const campos: Record<string, unknown> = {
    status_boleto_vigor: 'Pago',
    comprovante_pagamento_url: comprovanteUrl,
  };

  if (valorPago != null && valorPago > 0) campos.valor_pagamento = valorPago;

  if (ucBoletoUnico) {
    // Sem data: não acionar o repasse automático. A pendência fica visível.
    campos.status_repasse_equatorial = 'pendente';
  } else {
    // Meio-dia local evita que o fuso jogue a data para o dia anterior ao
    // virar timestamptz (mesma armadilha de parseLocalDate no export de faturas).
    campos.data_pgto_boleto = `${dataPagamento}T12:00:00`;
  }

  return campos;
}

/** Consulta a UC e monta o update da confirmação manual. */
export async function montarConfirmacaoPagamentoManual(
  ucCodigo: string | null | undefined,
  comprovanteUrl: string,
  dataPagamento: string,
  valorPago: number | null = null
): Promise<ResultadoConfirmacaoManual> {
  const ucBoletoUnico = await ucAderiuAoBoletoUnico(ucCodigo);
  return { camposUpdate: montarCamposConfirmacao(ucBoletoUnico, comprovanteUrl, dataPagamento, valorPago), ucBoletoUnico };
}

/**
 * Lê o valor digitado em reais: "1.787,84", "1787,84", "R$ 1.787,84" ou
 * "1787.84". Devolve null se não for um valor positivo.
 */
export function lerValorPago(texto: string): number | null {
  let s = String(texto ?? '').replace(/[^\d.,]/g, '');
  if (!s) return null;
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '');
  const valor = Number(s);
  return Number.isFinite(valor) && valor > 0 ? Math.round(valor * 100) / 100 : null;
}

/**
 * Valor do boleto, para já vir preenchido no campo. No boleto único é o total
 * (parte da Vigor + Equatorial) — o que o cliente paga e o que a baixa do banco
 * grava como `valor_pagamento`.
 */
export function valorDoBoleto(f: { valor_emissao_boleto_unico?: number | null; valor_boleto_vigor?: number | null }): number | null {
  const valor = Number(f.valor_emissao_boleto_unico ?? f.valor_boleto_vigor);
  return Number.isFinite(valor) && valor > 0 ? Math.round(valor * 100) / 100 : null;
}

/** Número em reais para o campo de texto: 1787.84 → "1.787,84". */
export function formatarValorPago(valor: number | null): string {
  return valor == null ? '' : valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Data de hoje em `yyyy-mm-dd` local — default do campo no formulário. */
export function hojeISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
